import React, { useState, useEffect } from 'react';
import { withRouter, useHistory } from 'react-router-dom';
import { PageContentLayout, PageHeaderLayout } from '../../../shared/layout';
import { Card, Row, Space, Table, Col, Modal, Spin, Tag, Typography } from 'antd';
import { FormOutlined } from '@ant-design/icons';
import Button from '../../../shared/globalStyling/Button';
import {
  ButtonText,
  PrimaryTableRowText,
  StandardTableRowText,
  TableColumnTitle,
} from '../../../shared/globalStyling/styledText';
import { StarOutlined } from '@ant-design/icons';
import FilterBar from './components/FilterBar';
import { formatCurrency2SigFig, formatWholeNumberNaN } from '../../../../core/utils/campaigns';
import LaunchAdvertiser from './components/LaunchAdvertiser';
import moment from 'moment';
import StatusTag from '../../Campaigns/shared/StatusTag';
import { useTagContext } from '../../../../core/components/tags/useTagContext';
const AdvertiserMainView = ({
  data,
  currentOrgId,
  onCreateAdvertiser,
  filteredLocations,
  filteredAudiences,
  isSelfService,
  orgLevel,
}) => {
  const history = useHistory();
  const [openLaunch, setOpenLaunch] = useState(false);
  const [tableData, setTableData] = useState(data);
  const [pageSize, setPageSize] = useState(10);
  const [current, setCurrent] = useState(1);
  const [sortedInfo, setSortedInfo] = useState({});

  useEffect(() => {
    const savedPageSize = sessionStorage.getItem('advertiserMainPageSize');
    if (savedPageSize) {
      setPageSize(Number(savedPageSize));
    }

    const savedSorting = sessionStorage.getItem('advertiserMainSorting');
    if (savedSorting) {
      try {
        const parsedSorting = JSON.parse(savedSorting);
        setSortedInfo(parsedSorting);
      } catch (error) {
        console.error('Failed to parse saved sorting:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (tableData && tableData.length > 0) {
      const savedPageSize = Number(sessionStorage.getItem('advertiserMainPageSize')) || 10;
      const savedCurrentPage = Number(sessionStorage.getItem('advertiserMainCurrentPage')) || 1;

      const maxPage = Math.ceil(tableData.length / savedPageSize);
      const validPage = savedCurrentPage <= maxPage ? savedCurrentPage : 1;

      if (validPage !== current) {
        setCurrent(validPage);
      }
    }
  }, [tableData, current]);

  const {
    fetchTags,
    tags,
    loadingTags,
    getBasicTagActivity,
    basicTagActivity,
    tagActivityLoading,
    tagActivityError,
    clearBasicTagActivity,
  } = useTagContext();

  const onFetchTags = () => {
    fetchTags({
      variables: {
        where: {
          orgs_some: {
            OR: [
              {
                parentOrg_some: {
                  id: currentOrgId,
                },
              },
              {
                id: currentOrgId,
              },
            ],
          },
          isDisabled: false,
        },
      },
    });
  };

  useEffect(() => {
    onFetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      title: 'Funnel Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortOrder: sortedInfo.columnKey === 'name' ? sortedInfo.order : null,
      render: (text) => (text != null ? text : '-'),
    },
    {
      title: <TableColumnTitle text={'Status'} />,
      dataIndex: 'status',
      key: 'status',
      width: '6%',
      align: 'left',
      footerContent: '',
      render: (text, record) => {
        return (
          <Row type="flex" justify="start">
            <Col>
              <StatusTag
                startDate={
                  record && moment(record.startDate).seconds(0).milliseconds(0).toISOString()
                    ? moment(record.startDate).seconds(0).milliseconds(0).toISOString()
                    : 'N/A'
                }
                endDate={
                  record && moment(record.endDate).seconds(0).milliseconds(0).toISOString()
                    ? moment(record.endDate).seconds(0).milliseconds(0).toISOString()
                    : 'N/A'
                }
                attributionWindow={30}
                statusCampaign={record.status && record.status}
              />
            </Col>
          </Row>
        );
      },
    },
    {
      title: 'Impressions',
      dataIndex: 'totalImpressions',
      key: 'totalImpressions',
      sorter: (a, b) => a.totalImpressions - b.totalImpressions,
      sortOrder: sortedInfo.columnKey === 'totalImpressions' ? sortedInfo.order : null,
      render: (text) => formatWholeNumberNaN(text),
    },
    {
      title: 'Sales',
      dataIndex: 'transactions',
      key: 'transactions',
      sorter: (a, b) => a.transactions - b.transactions,
      sortOrder: sortedInfo.columnKey === 'transactions' ? sortedInfo.order : null,
      render: (text, record) => {
        return (
          <Row type="flex" justify="start" align="middle">
            <Col style={{ minWidth: '50px' }}>
              <>
                <Row type="flex" gutter={[4, 4]}>
                  <Col>
                    <PrimaryTableRowText text={`Transactions:`} />
                  </Col>
                  <Col>
                    <StandardTableRowText text={formatWholeNumberNaN(text)} />
                  </Col>
                </Row>
                <Row type="flex" gutter={[4, 4]}>
                  <Col>
                    <PrimaryTableRowText text={`Revenue: `} />
                  </Col>
                  <Col>
                    <StandardTableRowText text={formatCurrency2SigFig(record.revenue)} />
                  </Col>
                </Row>
                <Row type="flex" gutter={[4, 4]}>
                  <Col>
                    <PrimaryTableRowText text={`Attr. Impressions: `} />
                  </Col>
                  <Col>
                    <StandardTableRowText text={formatWholeNumberNaN(record.impressions)} />
                  </Col>
                </Row>
              </>
            </Col>
          </Row>
        );
      },
    },
    {
      title: 'Signups',
      dataIndex: 'signUps',
      key: 'signUps',
      sorter: (a, b) => a.signUps - b.signUps,
      sortOrder: sortedInfo.columnKey === 'signUps' ? sortedInfo.order : null,
      render: (text, record) => {
        return (
          <Row type="flex" justify="start" align="middle">
            <Col style={{ minWidth: '50px' }}>
              <>
                <Row type="flex" gutter={[4, 4]}>
                  <Col>
                    <PrimaryTableRowText text={`Signups:`} />
                  </Col>
                  <Col>
                    <StandardTableRowText text={formatWholeNumberNaN(text)} />
                  </Col>
                </Row>
                <Row type="flex" gutter={[4, 4]}>
                  <Col>
                    <PrimaryTableRowText text={`Attr. Impressions: `} />
                  </Col>
                  <Col>
                    <StandardTableRowText
                      text={formatWholeNumberNaN(record.signUpsAttrImpressions)}
                    />
                  </Col>
                </Row>
              </>
            </Col>
          </Row>
        );
      },
    },
    {
      title: 'Sessions',
      dataIndex: 'sessions',
      key: 'sessions',
      sorter: (a, b) => a.sessions - b.sessions,
      sortOrder: sortedInfo.columnKey === 'sessions' ? sortedInfo.order : null,
      render: (text, record) => {
        return (
          <Row type="flex" justify="start" align="middle">
            <Col style={{ minWidth: '50px' }}>
              <>
                <Row type="flex" gutter={[4, 4]}>
                  <Col>
                    <PrimaryTableRowText text={`Sessions:`} />
                  </Col>
                  <Col>
                    <StandardTableRowText text={formatWholeNumberNaN(text)} />
                  </Col>
                </Row>
                <Row type="flex" gutter={[4, 4]}>
                  <Col>
                    <PrimaryTableRowText text={`Attr. Impressions: `} />
                  </Col>
                  <Col>
                    <StandardTableRowText
                      text={formatWholeNumberNaN(record.sessionsAttrImpressions)}
                    />
                  </Col>
                </Row>
              </>
            </Col>
          </Row>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <Modal
        title={`Create Advertiser Funnel`}
        visible={openLaunch}
        footer={null}
        width={'75%'}
        onCancel={() => {
          setOpenLaunch(false);
        }}
      >
        <LaunchAdvertiser
          currentOrgId={currentOrgId}
          tags={tags}
          open={openLaunch}
          onCreate={(values) => {
            setOpenLaunch(false);
            onCreateAdvertiser(values);
          }}
          filteredLocations={filteredLocations}
          filteredAudiences={filteredAudiences}
          isSelfService={isSelfService}
          onFetchTags={onFetchTags}
          loadingTags={loadingTags}
          getBasicTagActivity={getBasicTagActivity}
          basicTagActivity={basicTagActivity}
          tagActivityLoading={tagActivityLoading}
          tagActivityError={tagActivityError}
          clearBasicTagActivity={clearBasicTagActivity}
        />
      </Modal>
      <PageHeaderLayout
        title="Advertiser"
        titleIcon={
          <StarOutlined
            style={{
              marginRight: '10px',
              color: 'lightgrey',
              fontSize: 20,
            }}
          />
        }
        rightContent={
          <Row align="middle" justify="end">
            <Col flex="auto">
              <Space>
                <Button
                  onClick={() => {
                    setOpenLaunch(!openLaunch);
                  }}
                  type="primary"
                  ghost
                  icon={<FormOutlined />}
                >
                  <ButtonText text={'Add Funnel'} />
                </Button>
              </Space>
            </Col>
          </Row>
        }
      />
      <PageContentLayout>
        <Card
          title={
            <Row align="middle">
              <Col>
                <h3>Advertising Funnels</h3>
              </Col>
            </Row>
          }
          extra={
            <Row align="middle">
              <FilterBar orgLevel={orgLevel} setData={setTableData} data={data} />
            </Row>
          }
        >
          <Table
            pagination={{
              showSizeChanger: true,
              hideOnSinglePage: false,
              pageSize: pageSize,
              current: current,
              onChange: (page, size) => {
                setCurrent(page);
                sessionStorage.setItem('advertiserMainCurrentPage', page.toString());
                if (size !== pageSize) {
                  setPageSize(size);
                  sessionStorage.setItem('advertiserMainPageSize', size.toString());
                }
              },
              onShowSizeChange: (current, size) => {
                setPageSize(size);
                setCurrent(1);
                sessionStorage.setItem('advertiserMainCurrentPage', '1');
                sessionStorage.setItem('advertiserMainPageSize', size.toString());
              },
            }}
            columns={columns}
            dataSource={tableData}
            onChange={(pagination, filters, sorter, extra) => {
              if (sorter && sorter.columnKey) {
                const newSortedInfo = {
                  columnKey: sorter.columnKey,
                  order: sorter.order,
                };
                setSortedInfo(newSortedInfo);
                sessionStorage.setItem('advertiserMainSorting', JSON.stringify(newSortedInfo));
              } else {
                setSortedInfo({});
                sessionStorage.removeItem('advertiserMainSorting');
              }
            }}
            onRow={(record, rowIndex) => {
              return {
                onClick: (event) => {
                  history.push(`/advertiser/details/${record.id}`);
                },
              };
            }}
          />
        </Card>
      </PageContentLayout>
    </React.Fragment>
  );
};

export default withRouter(AdvertiserMainView);
